<script>
    import Select from "svelte-select/Select.svelte";

    import {
        Button,
        CardLayout,
        PageHead,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { allGroups, user } from "../../stores";

    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    import { SEARCH } from "../../Utils/utilConstants.svelte";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        getEmailValidation,
        getLandlineValidation,
        getPhoneValidation,
        getSearchSurgeonOptions,
        goBack,
        goToURL,
        isObjectEmpty,
        showError,
    } from "../../Utils/utilFunctions.svelte";
    import { onMount } from "svelte";

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserManager = $user.role == DirectUserRoles.Manager;

    $: isValidEmail = surgeryRoomDetails.contactEmail
        ? getEmailValidation(surgeryRoomDetails.contactEmail)
        : true;
    $: isValidPhone = surgeryRoomDetails.contactPhone
        ? getPhoneValidation(surgeryRoomDetails.contactPhone) ||
          getLandlineValidation(surgeryRoomDetails.contactPhone)
        : true;
    $: isValidSurgeon =
        selectedSurgeon &&
        !isObjectEmpty(selectedSurgeon) &&
        selectedSurgeon.uniqueId;
    $: isSaveBtnDisabled = !isValidEmail || !isValidPhone || !isValidSurgeon;

    let surgeryRoomDetails = {},
        isModifyingSurgeryRoom = false;

    let selectedSurgeon,
        filteredSurgeons = [],
        isLoadingSurgeons = false;

    let selectedGroup,
        filteredGroups = [],
        isLoadingGroups = false;

    onMount(() => {
        loadGroups();
    });

    const loadGroups = async () => {
        if (!isUserAdmin) return;

        if ($allGroups.length) {
            filteredGroups = $allGroups;
            return;
        }

        isLoadingGroups = true;

        await cacheAllGroups();

        isLoadingGroups = false;

        filteredGroups = $allGroups;
    };

    const createSurgeryRoom = async () => {
        _mappingSurgeonRoomDetails();

        isModifyingSurgeryRoom = true;

        await apiMethods.postMethod
            .createSurgeryRoom(surgeryRoomDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create surgery room, please refresh the page and try again and contact support if the issue persists.",
                    );
                else goToURL("/SurgeryRooms");
            });

        isModifyingSurgeryRoom = false;
    };

    const clearCurrentGroup = () => (selectedGroup = null);

    const handleSearchSurgeon = async (searchText = "") => {
        if (!searchText || searchText.length < 2) return [];

        isLoadingSurgeons = true;

        await apiMethods.getMethod
            .getAllProviders(0, 999999, { name: searchText })
            .then((res) => {
                if (!res.error) _filterSurgeon(res.results);
                else filteredSurgeons = [];
            });

        isLoadingSurgeons = false;
    };

    const handleSelectSurgeon = () => {
        const { firstName, lastName, state } = selectedSurgeon;

        surgeryRoomDetails.contactName = `${firstName} ${lastName}`;
        surgeryRoomDetails.state = state;
    };

    const clearCurrentSurgeon = () => (selectedSurgeon = null);

    // HELPER FUNCTIONS
    const _mappingSurgeonRoomDetails = () => {
        const { uniqueId } = selectedSurgeon;

        surgeryRoomDetails.providerUniqueId = uniqueId;

        if (isUserAdmin && selectedGroup && !isObjectEmpty(selectedGroup)) {
            const { id } = selectedGroup;
            surgeryRoomDetails.groupId = id;
        } else if (isUserManager) surgeryRoomDetails.groupId = $user.groupId;
    };

    const _filterSurgeon = (arr = []) => {
        if (!arr || !arr.length) return [];

        filteredSurgeons = getSearchSurgeonOptions(arr);
    };
</script>

<PageHead title="Create New Surgery Room" />

<CardLayout title="Surgery Room Details">
    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-auto">
            {#if isUserAdmin}
                <div class="flex items-center gap-x-4">
                    <label for="group" class="w-1/4">Group</label>
                    <div class="flex items-center flex-1">
                        <Select
                            bind:value={selectedGroup}
                            items={filteredGroups}
                            itemId="id"
                            label="name"
                            on:clear={clearCurrentGroup}
                            noOptionsMessage="No Groups found"
                            placeholder={isLoadingGroups
                                ? "Loading groups"
                                : "Select Group"}
                            loading={isLoadingGroups}
                            disabled={isLoadingGroups}
                        />
                    </div>
                </div>
            {/if}

            <div class="flex items-center gap-x-4">
                <label for="group" class="mandatory w-1/4">Surgeon</label>
                <div class="flex items-center flex-1">
                    <Select
                        bind:value={selectedSurgeon}
                        loadOptions={handleSearchSurgeon}
                        items={filteredSurgeons}
                        itemId="key"
                        label="displayName"
                        debounceWait={SEARCH.DEBOUNCE_TIME}
                        noOptionsMessage="No Surgeons found"
                        placeholder={isLoadingSurgeons
                            ? "Loading"
                            : "Select Surgeon"}
                        loading={isLoadingSurgeons}
                        on:clear={clearCurrentSurgeon}
                        on:select={handleSelectSurgeon}
                    />
                </div>
            </div>

            <div class="flex items-center gap-x-4">
                <label for="contactName" class="w-1/4">Contact Name</label>
                <input
                    type="text"
                    name="contactName"
                    class="input input-sm input-bordered flex-1"
                    id="contactName"
                    bind:value={surgeryRoomDetails.contactName}
                />
            </div>

            <div class="flex items-center gap-x-4">
                <label for="preferredName" class="w-1/4">Preferred Name</label>
                <input
                    type="text"
                    name="preferredName"
                    class="input input-sm input-bordered flex-1"
                    id="preferredName"
                    bind:value={surgeryRoomDetails.preferredName}
                />
            </div>

            <div class="flex flex-col gap-y-1">
                <div class="flex items-center gap-x-4">
                    <label for="contactEmail" class="w-1/4">Contact Email</label
                    >
                    <input
                        type="email"
                        name="contactEmail"
                        class="input input-sm input-bordered flex-1"
                        id="contactEmail"
                        bind:value={surgeryRoomDetails.contactEmail}
                    />
                </div>

                {#if !isValidEmail}
                    <div class="flex items-center gap-x-4">
                        <label for="" class="w-1/4" />
                        <p class="flex-1 text-error">Invalid Email Address</p>
                    </div>
                {/if}
            </div>

            <div class="flex flex-col gap-y-1">
                <div class="flex items-center gap-x-4">
                    <label for="contactPhone" class="w-1/4">Contact Phone</label
                    >
                    <input
                        type="phone"
                        name="contactPhone"
                        class="input input-sm input-bordered flex-1"
                        id="contactPhone"
                        bind:value={surgeryRoomDetails.contactPhone}
                    />
                </div>

                {#if !isValidPhone}
                    <div class="flex items-center gap-x-4">
                        <label for="" class="w-1/4" />
                        <p class="flex-1 text-error">Invalid Contact Phone</p>
                    </div>
                {/if}
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-auto">
            <div class="flex items-center gap-x-4">
                <label for="location" class="w-1/4">Location</label>
                <input
                    type="text"
                    name="location"
                    class="input input-sm input-bordered flex-1"
                    id="location"
                    bind:value={surgeryRoomDetails.location}
                />
            </div>

            <div class="flex items-center gap-x-4">
                <label for="city" class="w-1/4">City</label>
                <input
                    type="text"
                    name="city"
                    class="input input-sm input-bordered flex-1"
                    id="city"
                    bind:value={surgeryRoomDetails.city}
                />
            </div>

            <div class="flex items-center gap-x-4">
                <label for="state" class="w-1/4">State</label>
                <input
                    type="text"
                    name="state"
                    class="input input-sm input-bordered flex-1"
                    id="state"
                    bind:value={surgeryRoomDetails.state}
                />
            </div>

            <div class="flex items-center gap-x-4">
                <label for="postCode" class="w-1/4">Post Code</label>
                <input
                    type="text"
                    name="postCode"
                    class="input input-sm input-bordered flex-1"
                    id="postCode"
                    bind:value={surgeryRoomDetails.postCode}
                />
            </div>
        </div>
    </div>

    <div class="flex flex-1 items-center justify-end p-4 gap-4">
        <Button clickFunc={goBack} manualClass="w-[64px] btn-error">
            Cancel
        </Button>

        <Button
            clickFunc={createSurgeryRoom}
            manualClass={`w-[64px] ${isSaveBtnDisabled ? "text-primary" : ""} ${isModifyingSurgeryRoom ? "loading" : ""}`}
            isDisabled={isSaveBtnDisabled}
        >
            {isModifyingSurgeryRoom ? "Creating" : "Create"}
        </Button>
    </div>
</CardLayout>

<style>
    .mandatory::after {
        content: "*";
    }
</style>
